import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from '@/components/LocalizedLink';
import { ArrowLeft, Clock, Tag, Share2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { SafeHtml } from '@/components/SafeHtml';
import { useApiData } from '@/hooks/useApiData';

const NO_ITEMS = [];
const PROSE = `mt-10 prose prose-earth max-w-none font-sans text-earth/70 leading-relaxed
  prose-headings:font-display prose-headings:text-earth-dark prose-headings:tracking-tight
  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
  prose-p:mb-4 prose-p:text-base
  prose-strong:text-earth-dark prose-strong:font-semibold
  prose-a:text-saffron prose-a:no-underline hover:prose-a:underline`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const postReq = useApiData(`/api/blog/${slug}`, null);
  const allReq = useApiData('/api/blog', NO_ITEMS);
  const post = postReq.data;
  const relatedPosts = useMemo(() => allReq.data.filter(p => p.slug !== slug).slice(0, 2), [allReq.data, slug]);

  if (postReq.loading || allReq.loading) return <div className="min-h-screen pt-32 flex items-center justify-center text-earth/30 font-sans">Loading...</div>;
  if (!post) return (
    <div className="min-h-screen pt-32 flex flex-col items-center justify-center gap-4">
      <p className="text-earth/50 font-sans text-lg">Post not found</p>
      <Link to="/blog" className="text-saffron font-sans font-semibold text-sm">Back to Blog</Link>
    </div>
  );

  return (
    <div data-testid="blog-post-page">

      {/* Header */}
      <section className="pt-28 pb-4 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <Link to="/blog" className="inline-flex items-center gap-2 text-earth/50 hover:text-saffron font-sans text-sm transition-colors" data-testid="back-to-blog">
          <ArrowLeft size={14} /> Back to Blog
        </Link>
      </section>

      {/* Article */}
      <article className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            {/* Meta */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <span className="flex items-center gap-1.5 text-xs text-earth/40 font-sans">
                <Clock size={12} />
                {new Date(post.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              {post.tags?.map(tag => (
                <span key={tag} className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-saffron font-sans bg-saffron/5 px-2.5 py-1 rounded">
                  <Tag size={9} /> {tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-earth-dark leading-tight tracking-tight" data-testid="blog-post-title">
              {post.title}
            </h1>
            <p className="mt-4 text-lg text-earth/60 font-sans leading-relaxed">{post.excerpt}</p>

            {/* Cover Image */}
            {post.cover_image && (
              <div className="mt-8 rounded-lg overflow-hidden shadow-card-lift">
                <img src={post.cover_image} alt={post.title} width={1200} height={600} className="w-full aspect-[2/1] object-cover" />
              </div>
            )}

            {/* Content */}
            <SafeHtml html={post.content} className={PROSE} testId="blog-post-content" />

            {/* Share */}
            <div className="mt-10 pt-6 border-t border-earth/10 flex items-center gap-3">
              <Share2 size={14} className="text-earth/30" />
              <span className="text-xs text-earth/40 font-sans">Share this article:</span>
              <a href={`https://wa.me/?text=${encodeURIComponent(post.title + ' - https://www.asjklaxmiglobal.com/blog/' + slug)}`} target="_blank" rel="noopener noreferrer" className="text-xs text-saffron font-sans hover:text-saffron-dark transition-colors">WhatsApp</a>
              <a href={`mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent('Check out this article: https://www.asjklaxmiglobal.com/blog/' + slug)}`} className="text-xs text-saffron font-sans hover:text-saffron-dark transition-colors">Email</a>
            </div>

            {/* CTA */}
            <div className="mt-10 bg-saffron/5 border border-saffron/20 rounded-lg p-8 text-center">
              <h3 className="font-display text-xl font-bold text-earth-dark">Need a Reliable Spice Exporter?</h3>
              <p className="text-sm text-earth/60 font-sans mt-2">Get competitive quotes for premium Indian spices. FSSAI certified, lab-tested quality.</p>
              <Link to="/contact" className="mt-4 inline-flex items-center gap-2 bg-saffron text-white font-sans font-semibold text-sm px-6 py-3 rounded-md hover:bg-saffron-dark transition-colors" data-testid="blog-cta">
                Request Quote <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-saffron-50/30" data-testid="related-posts">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display text-2xl font-bold text-earth-dark mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {relatedPosts.map(rp => (
                <Link key={rp.slug} to={`/blog/${rp.slug}`} className="group bg-white rounded-lg overflow-hidden shadow-card-lift hover:shadow-card-hover transition-all border border-saffron-100/50" data-testid={`related-${rp.slug}`}>
                  {rp.cover_image && (
                    <img src={rp.cover_image} alt={rp.title} width={800} height={450} className="w-full aspect-[16/9] object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  )}
                  <div className="p-5">
                    <h3 className="font-display text-base font-bold text-earth-dark group-hover:text-saffron transition-colors">{rp.title}</h3>
                    <p className="text-xs text-earth/50 font-sans mt-1 line-clamp-2">{rp.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
